// src/routes/routeConfig.ts
import { lazy } from "react";

// Lazy loaded components
const VideoLayout = lazy(
  () => import("../components/newconference/VideoLayout")
);
const Layout = lazy(() => import("../components/layouts/Layout"));
const LoginPage = lazy(() => import("../components/Login/Login"));
const LandingPage = lazy(
  () => import("../components/Landing_Page/MainContent")
);
const ContactUs = lazy(() => import("../components/ContactUs"));
const AboutUs = lazy(() => import("../components/AboutUs"));
const PricingPlans = lazy(() => import("../components/Pricing"));
const CoursesPage = lazy(() => import("../components/Landing_Page/CoursePage"));
const PublicCourseDetail = lazy(() => import("../components/Landing_Page/PublicCourseDetail"));
const VerifyEmailInstructions = lazy(() => import("../components/common/ui/VerifyEmailInstructions"));
const ForgotPassword = lazy(() => import("../components/auth/ForgotPassword"));
const ResetPassword  = lazy(() => import("../components/auth/ResetPassword"));
const VerifyOrganizationEmailInstructions = lazy(() => import("../components/common/ui/VerifyOrganizationEmailInstructions"));
const VerifyEmail = lazy(() => import("../components/common/ui/VerifyEmail"))
const VerifyOrganizationEmail = lazy(
  () => import("../components/common/ui/VerifyOrganizationEmail")
)
const VerifyTutorEmailInstructions = lazy(
  () => import("../components/common/ui/VerifyTutorEmailInstructions")
)

const StudentEmailSearch = lazy(
  () => import("../components/Landing_Page/FindStudent")
);
const UnauthorizedAccess = lazy(
  () => import("../components/common/UnauthorizedAccess")
);

// Dashboard layouts
const StudentDashboardLayout = lazy(
  () => import("../components/layouts/StudentLayout")
);
const TeachersDashboardLayout = lazy(
  () => import("../components/layouts/teachersLayout")
);
const OrganizationLayout = lazy(
  () => import("../components/layouts/OrganisationLayout")
);

// Student dashboard components
const StudentDashboardHome = lazy(
  () => import("../components/studentdashboard/StudentHome")
);
const StudentCourses = lazy(
  () => import("../components/studentdashboard/StudentCourses")
);
const CourseDetails = lazy(
  () => import("../components/studentdashboard/StudentCourseDetails")
);
const CourseLearnPage = lazy(
  () => import("../components/studentdashboard/CourseLearnPage")
);
const SessionSchedule = lazy(
  () => import("../components/studentdashboard/SessionScheduled")
);
const StudentTasks = lazy(
  () => import("../components/studentdashboard/StudentTasks")
);
const TaskDetails = lazy(
  () => import("../components/studentdashboard/TaskDetails")
);
const LibrarySection = lazy(
  () => import("../components/studentdashboard/StudentLibrary")
);
const StudentProfile = lazy(
  () => import("../components/studentdashboard/StudentProfile")
);
const StudentTest = lazy(
  () => import("../components/studentdashboard/StudentTest")
);
const StudentTemplateGrid = lazy(
  () => import("../components/studentdashboard/StudentTemplateGrid")
);
const TestResultsPage = lazy(
  () => import("../components/studentdashboard/TestResultsSummary")
);
const TestResult = lazy(
  () => import("../components/studentdashboard/TestResult")
);
const TestSummaryPage = lazy(
  () => import("../components/studentdashboard/TestSummaryPage")
);

const AnnouncementDetails = lazy(() => import("../components/studentdashboard/AnnouncementDetails"));
const NotificationsPage = lazy(() => import("../components/notifications/NotificationsPage"));

// Teacher dashboard components
const TeacherDashboardHome = lazy(
  () => import("../components/teachersdashboard/TutorHome")
);
const Courses = lazy(() => import("../components/teachersdashboard/Courses"));
const TeachersCourseDetails = lazy(
  () => import("../components/teachersdashboard/CourseDetails")
);
const CourseEdit = lazy(
  () => import("../components/teachersdashboard/CourseEdit")
);  
const QATemplateBuilder = lazy(
  () => import("../components/teachersdashboard/QATemplate")
);
const CourseForm = lazy(
  () => import("../components/teachersdashboard/CourseForm")
);
const TaskList = lazy(() => import("../components/teachersdashboard/Task"));
const TaskSubmissions = lazy(
  () => import("../components/teachersdashboard/TaskSubmissions")
);
const ViewSubmission = lazy(
  () => import("../components/teachersdashboard/ViewSubmision")
);
const DigitalLibrary = lazy(
  () => import("../components/teachersdashboard/Library")
);
const UpcomingClasses = lazy(
  () => import("../components/teachersdashboard/Schedule")
);
const TutorProfile = lazy(
  () => import("../components/teachersdashboard/TutorProfile")
);
const TeacherTemplateGrid = lazy(
  () => import("../components/teachersdashboard/TeachersTemplateGrid")
);
const PaymentAccountSetup = lazy(
  () => import("../components/payments/PaymentSetUp")
);
const StripeOnboardingComplete = lazy(
  () => import("../components/payments/StripeOnboardingComplete")
);
const AnnouncementsList = lazy(
  () => import("../components/announcement/AnnouncementList")
);

// Organization dashboard components
const OrganizationHome = lazy(
  () => import("../components/organisationdashboard/OrganisationDashboard")
);
const DepartmentsManagement = lazy(
  () => import("../components/organisationdashboard/DepartmentsManagement")
);
const OrgTutorDetail = lazy(
  () => import("../components/organisationdashboard/OrgTutorDetail")
);
const OrgStudentDetail = lazy(
  () => import("../components/organisationdashboard/OrgStudentDetail")
);
const StudentManagement = lazy(
  () => import("../components/organisationdashboard/StudentManagement")
);
const TeachersManagement = lazy(
  () => import("../components/organisationdashboard/TeachersManagement")
);
const AnalyticsPage = lazy(
  () => import("../components/organisationdashboard/Analytics")
);
const CourseManagement = lazy(
  () => import("../components/organisationdashboard/CourseManagement")
);
const SettingsPage = lazy(
  () => import("../components/organisationdashboard/Settings")
);

// Payment components
const CourseCheckout = lazy(
  () => import("../components/payments/CourseCheckout")
);
const PaymentProcessingPage = lazy(
  () => import("../components/payments/PaymentProcessing")
);
const PaymentSuccessPage = lazy(
  () => import("../components/payments/PaymentSuccess")
);
const PaymentFailedPage = lazy(
  () => import("../components/payments/PaymentFailed")
);
const PaystackCallback = lazy(
  () => import("../components/payments/PaystackCallback")
);

// Video dashboard components
const VideoDashboard = lazy(() => import("../components/newconference/Rooms"));

const Whiteboard = lazy(
  () => import("../components/whiteboard/VirtualWhiteboard")
);

const PerformanceAnalytics = lazy(() => import("../components/ReportCards"));
const StudentReportPage = lazy(
  () => import("../components/studentdashboard/StudentReportCard")
);
// Issuer-side certificate form (tutor / org routes)
const CertificatesPage = lazy(
  () => import("../components/CertificateGenerator")
);
// Student: view own certificates
const StudentCertificatesPage = lazy(
  () => import("../components/studentdashboard/StudentCertificatesPage")
);
// Public certificate verification
const VerifyCertificatePage = lazy(
  () => import("../components/VerifyCertificatePage")
);
// Admin: certificate management
const AdminCertificatesPage = lazy(
  () => import("../components/admin/certificates/AdminCertificatesPage")
);
const ScoreInput = lazy(
  () => import("../components/score-input/ScoreInputPage")
);
const ReportCards = lazy(
  () => import("../components/ReportAnalytics/ReportCardPage")
);


// Admin components
const AdminLogin = lazy(() => import("../components/admin/AdminLogin"));
const AdminLayout = lazy(() => import("../components/layouts/AdminLayout"));
const AdminDashboard = lazy(() => import("../components/admin/AdminDashboard"));
const OrganizationList = lazy(() => import("../components/admin/organizations/OrganizationList"));
const OrganizationDetail = lazy(() => import("../components/admin/organizations/OrganizationDetail"));
const UserDirectory = lazy(() => import("../components/admin/users/UserDirectory"));
const AdminUserDetail = lazy(() => import("../components/admin/users/AdminUserDetail"));
const FinanceDashboard = lazy(() => import("../components/admin/finance/FinanceDashboard"));
const ContentModeration = lazy(() => import("../components/admin/content/ContentModeration"));
const Communications = lazy(() => import("../components/admin/communications/Communications"));
const AdminSettings = lazy(() => import("../components/admin/settings/AdminSettings"));
const TutorApproval = lazy(() => import("../components/admin/tutors/TutorApproval"));

// Route configuration
export const routes = [
  // ✅ Public routes - All children are also public
  {
    path: "/",
    element: Layout,
    protected: false,
    children: [
      { index: true, element: LandingPage, protected: false },
      { path: "pricing", element: PricingPlans, protected: false },
      { path: "about-us", element: AboutUs, protected: false },
      { path: "contact-us", element: ContactUs, protected: false },
      { path: "courses", element: CoursesPage, protected: false },
      { path: "courses/find-student", element: StudentEmailSearch, protected: false },
      { path: "courses/:id", element: PublicCourseDetail, protected: false },
      { path: "login", element: LoginPage, protected: false },
      { path: "forgot-password", element: ForgotPassword, protected: false },
      { path: "reset-password",  element: ResetPassword,  protected: false },
      { path: "admin-login", element: AdminLogin, protected: false },
      { path: "unauthorized", element: UnauthorizedAccess, protected: false },
      {
        path: "signup/:role",
        element: "REGISTER_FORM",
        protected: false,
      },
      {
        path: "verify-tutor-email-instructions",
        element: VerifyTutorEmailInstructions,
        wrapWithElements: true,
        protected: false,
      },
      {
        path: "verify-org-email-instructions",
        element: VerifyOrganizationEmailInstructions,
        wrapWithElements: true,
        protected: false,
      },
      {
        path: "verify-email-instructions",
        element: VerifyEmailInstructions,
        wrapWithElements: true,
        protected: false,
      },
      {
        path: "verify-email",
        element: VerifyEmail,
        wrapWithElements: true,
        protected: false,
      },
      {
        path: "verify-org-email",
        element: VerifyOrganizationEmail,
        wrapWithElements: true,
        protected: false,
      },

      // Payment routes - can be accessed by anyone
      {
        path: "course/:courseId/checkout",
        element: CourseCheckout,
        wrapWithElements: true,
        protected: false,
      },
      {
        path: "courses/:courseId/processing/:paymentId",
        element: PaymentProcessingPage,
        protected: false,
      },
      { path: "courses/:courseId/success", element: PaymentSuccessPage, protected: false },
      { path: "courses/:courseId/failed", element: PaymentFailedPage, protected: false },
      { path: "paystack/callback", element: PaystackCallback, protected: false },
      // Public certificate verification — no login required
      { path: "verify-certificate/:certificateId", element: VerifyCertificatePage, protected: false },
      { path: "verify-certificate", element: VerifyCertificatePage, protected: false },
    ],
  },

  // ✅ Student dashboard routes - Only students can access
  {
    path: "studentdashboard",
    element: StudentDashboardLayout,
    protected: true,
    allowedRoles: ['student'],
    children: [
      { index: true, element: StudentDashboardHome, protected: true },
      { path: "courses", element: StudentCourses, protected: true },
      { path: ":courseId/course-details", element: CourseDetails, protected: true },
      { path: ":courseId/learn/:contentId", element: CourseLearnPage, protected: true },
      { path: ":courseId/learn", element: CourseLearnPage, protected: true },
      { path: "sessions", element: SessionSchedule, protected: true },
      { path: "tasks", element: StudentTasks, protected: true },
      { path: "task-details", element: TaskDetails, protected: true },
      { path: "library", element: LibrarySection, protected: true },
      { path: "profile", element: StudentProfile, protected: true },
      { path: "test/:templateId", element: StudentTest, protected: true },
      { path: "qa/templates", element: StudentTemplateGrid, protected: true },
      { path: "qa", element: StudentTest, protected: true },
      { path: "test-results", element: TestResultsPage, protected: true },
      { path: "test-summary/:testHistoryId", element: TestSummaryPage, protected: true },
      { path: "test-result/:sessionId", element: TestResult, protected: true },
      { path: "certificates", element: StudentCertificatesPage, protected: true },
      { path: "announcements/:announcementId", element: AnnouncementDetails, protected: true },
      { path: "notifications", element: NotificationsPage, protected: true },
    ],
  },

  // ✅ Teacher dashboard routes - Only tutors/teachers can access
  {
    path: "teachersdashboard",
    element: TeachersDashboardLayout,
    protected: true,
    allowedRoles: ['tutor', 'teacher'],
    children: [
      { index: true, element: TeacherDashboardHome, protected: true },
      { path: "courses", element: Courses, protected: true },
      { path: "course-details", element: TeachersCourseDetails, protected: true },
      { path: "courses/edit/:courseId", element: CourseEdit, protected: true },
      { path: "qa/create", element: QATemplateBuilder, protected: true },
      { path: "courses/form", element: CourseForm, protected: true },
      { path: "tasks", element: TaskList, protected: true },
      { path: "tasks/submissions", element: TaskSubmissions, protected: true },
      { path: "tasks/view-submissions", element: ViewSubmission, protected: true },
      { path: "library", element: DigitalLibrary, protected: true },
      { path: "schedule", element: UpcomingClasses, protected: true },
      { path: "profile", element: TutorProfile, protected: true },
      { path: "qa/templates", element: TeacherTemplateGrid, protected: true },
      { path: "payment-setup", element: PaymentAccountSetup, protected: true },
      { path: "stripe/complete-onboarding", element: StripeOnboardingComplete, protected: true },
      { path: "announcement/list", element: AnnouncementsList, protected: true },
      { path: "notifications", element: NotificationsPage, protected: true },
      {
        path: "report-card/:studentId/:courseId",
        element: PerformanceAnalytics,
        protected: true,
      },
      { path: "report-card/:studentId/", element: StudentReportPage, protected: true },
      {
        path: "certificate/:studentId/:courseId/:tutorId/:studentEmail",
        element: CertificatesPage,
        protected: true,
      },
      // View all certificates issued by this tutor
      { path: "certificates", element: AdminCertificatesPage, protected: true },
    ],
  },

  // ✅ Organization dashboard routes - Only organizations can access
  {
    path: "organization",
    element: OrganizationLayout,
    protected: true,
    allowedRoles: ['organization'],
    children: [
      { index: true, element: "REDIRECT_ORG_DASHBOARD", protected: true },
      { path: "dashboard", element: OrganizationHome, protected: true },
      { path: "students", element: StudentManagement, protected: true },
      { path: "students/:studentId", element: OrgStudentDetail, protected: true },
      { path: "teachers", element: TeachersManagement, protected: true },
      { path: "teachers/:tutorId", element: OrgTutorDetail, protected: true },
      { path: "analytics", element: AnalyticsPage, protected: true },
      { path: "courses", element: CourseManagement, protected: true },
      { path: "settings", element: SettingsPage, protected: true },
      { path: "payment-setup", element: PaymentAccountSetup, protected: true },
      { path: "stripe/complete-onboarding", element: StripeOnboardingComplete, protected: true },
      { path: "announcements", element: AnnouncementsList, protected: true },
      { path: "notifications", element: NotificationsPage, protected: true },
      {
        path: "certificate/:studentId/:courseId/:organizationId/:studentEmail",
        element: CertificatesPage,
        protected: true,
      },
      // View all certificates issued by this organisation
      { path: "certificates", element: AdminCertificatesPage, protected: true },
      { path: "departments", element: DepartmentsManagement, protected: true },
      { path: "score-input", element: ScoreInput, protected: true },
      { path: "result-cards", element: ReportCards, protected: true },
    ],
  },

  // ✅ Admin routes - Only admin role can access
  {
    path: "admin",
    element: AdminLayout,
    protected: true,
    allowedRoles: ['admin'],
    children: [
      { index: true, element: "REDIRECT_ADMIN_DASHBOARD", protected: true },
      { path: "dashboard", element: AdminDashboard, protected: true },
      { path: "organizations", element: OrganizationList, protected: true },
      { path: "organizations/:id", element: OrganizationDetail, protected: true },
      { path: "users", element: UserDirectory, protected: true },
      { path: "users/:id", element: AdminUserDetail, protected: true },
      { path: "tutors", element: TutorApproval, protected: true },
      { path: "finance", element: FinanceDashboard, protected: true },
      { path: "content", element: ContentModeration, protected: true },
      { path: "communications", element: Communications, protected: true },
      { path: "settings", element: AdminSettings, protected: true },
      { path: "notifications", element: NotificationsPage, protected: true },
      { path: "certificates", element: AdminCertificatesPage, protected: true },
    ],
  },

  // ✅ Conference routes - Protected, any authenticated user can access
  {
    path: "conference",
    element: VideoLayout,
    protected: true, // Requires authentication but no specific role
    children: [
      { path: "classroom/:roomId", element: VideoDashboard, protected: true },
      { path: "whiteboard/:roomId", element: Whiteboard, protected: true },
    ],
  },
];